import { FormEvent, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { toastModule } from "@/modules/toast";
import { validationModule } from "@/modules/validation";
import {
  CreateAudioRecordingInternalRequestSchema,
  createAudioRecordingInternal,
} from "@/pages/audio-recordings/shared/api";

const Container = styled.div`
  padding: 32px;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #4a5568;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Input = styled.input`
  border: 1px solid #cbd5f5;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  border: 1px solid #cbd5f5;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  min-height: 140px;
  font-family: "JetBrains Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const AudioRecordingsCreatePage = () => {
  const navigate = useNavigate();
  const [spaceAccountId, setSpaceAccountId] = useState("");
  const [associatedNoteId, setAssociatedNoteId] = useState("");
  const [associatedClientId, setAssociatedClientId] = useState("");
  const [valueJsonInput, setValueJsonInput] = useState('{"title":"New audio recording"}');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedSpaceAccountId = spaceAccountId.trim();

    if (!validationModule.isUuid(trimmedSpaceAccountId)) {
      toastModule.triggerToast({ content: "Space account ID must be a UUID." });
      return;
    }

    const trimmedNoteId = associatedNoteId.trim();
    const trimmedClientId = associatedClientId.trim();

    let parsedValue: Record<string, unknown> | undefined;
    if (valueJsonInput.trim().length > 0) {
      try {
        parsedValue = JSON.parse(valueJsonInput);
      } catch (_error) {
        toastModule.triggerToast({ content: "Value JSON must be valid JSON." });
        return;
      }
    }

    const payload: CreateAudioRecordingInternalRequestSchema = {
      space_account_id: trimmedSpaceAccountId,
      associated_note_id: trimmedNoteId ? trimmedNoteId : undefined,
      associated_client_id: trimmedClientId ? trimmedClientId : undefined,
      value_json: parsedValue,
    };

    setIsSubmitting(true);
    try {
      const audioRecording = await createAudioRecordingInternal(payload);
      if (!audioRecording) {
        throw new Error("Missing audio recording payload");
      }

      toastModule.triggerToast({ content: "Audio recording created." });
      navigate(
        atlasRoutes.audioRecordingsView({ params: { audioRecordingId: audioRecording.id } }).path
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create audio recording.";
      toastModule.triggerToast({ content: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <div>
        <Title>Create Audio Recording</Title>
        <Subtitle>
          Provide the destination space account (and optional note/client associations). We will
          emit the sync model and deterministic CapturedAudio container immediately.
        </Subtitle>
      </div>

      <Form onSubmit={handleSubmit}>
        <Field>
          <Label htmlFor="space-account-id">Space Account ID</Label>
          <Input
            id="space-account-id"
            value={spaceAccountId}
            onChange={event => setSpaceAccountId(event.target.value)}
            placeholder="space-account UUID"
            required
          />
        </Field>

        <Field>
          <Label htmlFor="associated-note-id">Associated Note ID (optional)</Label>
          <Input
            id="associated-note-id"
            value={associatedNoteId}
            onChange={event => setAssociatedNoteId(event.target.value)}
            placeholder="note UUID"
          />
        </Field>

        <Field>
          <Label htmlFor="associated-client-id">Associated Client ID (optional)</Label>
          <Input
            id="associated-client-id"
            value={associatedClientId}
            onChange={event => setAssociatedClientId(event.target.value)}
            placeholder="client UUID"
          />
        </Field>

        <Field>
          <Label htmlFor="value-json">Value JSON (optional)</Label>
          <TextArea
            id="value-json"
            value={valueJsonInput}
            onChange={event => setValueJsonInput(event.target.value)}
          />
          <Subtitle>
            Provide arbitrary metadata that will be persisted to the sync model's value_json column.
          </Subtitle>
        </Field>

        <ButtonRow>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create"}
          </Button>
          <Button
            type="button"
            variant="soft"
            onClick={() => navigate(atlasRoutes.audioRecordings({}).path)}
          >
            Cancel
          </Button>
        </ButtonRow>
      </Form>
    </Container>
  );
};
